import { Modal } from "flowbite-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  HiChevronLeft,
  HiChevronRight,
  HiOutlineX,
} from "react-icons/hi";
import ProdutoAcoes from "./ProdutoAcoes";

const ordenarFotosComCapaPrimeiro = (fotos = []) =>
  [...fotos].sort((a, b) => {
    if (a?.e_capa && !b?.e_capa) return -1;
    if (!a?.e_capa && b?.e_capa) return 1;
    return new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
  });

const formatarMoeda = (valor) =>
  `R$ ${Number(valor || 0).toFixed(2).replace(".", ",")}`;

const calcularPrecoPromocional = (produto) => {
  const valorBase = Number(produto?.valor);
  if (!Number.isFinite(valorBase)) return null;

  if (
    produto?.valor_promocional !== null &&
    produto?.valor_promocional !== undefined &&
    produto?.valor_promocional !== ""
  ) {
    const valorPromocional = Number(produto.valor_promocional);
    if (!Number.isFinite(valorPromocional)) return null;

    const percentual = Math.round(
      ((valorBase - valorPromocional) / valorBase) * 100
    );

    return {
      valorBase,
      valorFinal: valorPromocional,
      percentual: Math.max(percentual, 0),
    };
  }

  if (
    produto?.desconto !== null &&
    produto?.desconto !== undefined &&
    produto?.desconto !== ""
  ) {
    const desconto = Number(produto.desconto);
    if (!Number.isFinite(desconto)) return null;

    return {
      valorBase,
      valorFinal: valorBase * (1 - desconto / 100),
      percentual: Math.max(Math.round(desconto), 0),
    };
  }

  return null;
};

const ProdutoModalClassico = ({ produto, onClose }) => {
  const fotosOrdenadas = useMemo(
    () => ordenarFotosComCapaPrimeiro(produto?.fotos || []),
    [produto?.fotos]
  );

  const [imagemSelecionada, setImagemSelecionada] = useState(
    fotosOrdenadas[0]?.url || null
  );

  useEffect(() => {
    setImagemSelecionada(fotosOrdenadas[0]?.url || null);
  }, [produto?.id, fotosOrdenadas]);

  if (!produto) return null;

  const precoPromocional = calcularPrecoPromocional(produto);
  const temValorBase =
    produto?.valor !== null &&
    produto?.valor !== undefined &&
    produto?.valor !== "";
  const temParcelas =
    produto?.maximo_parcelas !== null &&
    produto?.maximo_parcelas !== undefined &&
    produto?.maximo_parcelas !== "";

  return (
    <Modal show={!!produto} className="mt-8" size="5xl" onClose={onClose}>
      <Modal.Header>{produto.descricao}</Modal.Header>
      <Modal.Body className="px-5 py-5 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] lg:items-start">
          <div className="space-y-4">
            <div className="flex min-h-[320px] items-center justify-center rounded-2xl bg-gray-50 sm:min-h-[420px]">
              {imagemSelecionada ? (
                <img
                  src={`${process.env.REACT_APP_UPLOADS_URL}${imagemSelecionada}`}
                  alt="Imagem principal"
                  className="max-h-[56vh] w-full rounded-xl object-contain"
                />
              ) : (
                <div className="grid min-h-[260px] place-items-center rounded-xl text-sm text-gray-500">
                  Produto sem foto
                </div>
              )}
            </div>

            {fotosOrdenadas.length > 1 ? (
              <div className="overflow-x-auto">
                <div className="flex w-full gap-2 pb-1">
                  {fotosOrdenadas.map((foto) => (
                    <button
                      key={foto.id || foto.url}
                      type="button"
                      onClick={() => setImagemSelecionada(foto.url)}
                      className={`h-16 w-12 flex-shrink-0 overflow-hidden rounded-lg ring-2 transition sm:h-20 sm:w-16 ${
                        imagemSelecionada === foto.url
                          ? "opacity-100 ring-gray-900"
                          : "opacity-70 ring-transparent hover:opacity-100"
                      }`}
                    >
                      <img
                        src={`${process.env.REACT_APP_UPLOADS_URL}${foto.url}`}
                        alt="Miniatura"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-4 lg:pt-1">
            {produto.marca || produto.desconto !== null || precoPromocional ? (
              <div className="flex flex-wrap items-center gap-2">
                {produto.marca ? (
                  <span className="rounded-full bg-gray-200 px-3 py-1 text-sm font-medium leading-tight text-gray-800">
                    {produto.marca}
                  </span>
                ) : null}
                {precoPromocional ? (
                  <span className="rounded-full bg-black px-3 py-1 text-sm font-semibold leading-tight text-white">
                    {precoPromocional.percentual}% OFF
                  </span>
                ) : null}
                {!precoPromocional && produto.desconto !== null ? (
                  <span className="rounded-full bg-black px-3 py-1 text-sm font-semibold leading-tight text-white">
                    {produto.desconto}% OFF
                  </span>
                ) : null}
              </div>
            ) : null}

            {temValorBase || temParcelas ? (
              <div className="space-y-1">
                {temValorBase && temParcelas ? (
                  <>
                    <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-base font-semibold text-green-800">
                      {produto.maximo_parcelas}x de{" "}
                      {formatarMoeda(produto.valor / produto.maximo_parcelas)}
                    </span>

                    {precoPromocional ? (
                      <div className="text-xs text-gray-500">
                        ou 1x de{" "}
                        <span className="font-semibold text-gray-700">
                          {formatarMoeda(precoPromocional.valorFinal)}
                        </span>
                      </div>
                    ) : produto.desconto !== null ? (
                      <div className="text-xs text-gray-500">
                        ou 1x de{" "}
                        <span className="font-semibold text-gray-700">
                          {formatarMoeda(produto.valor * (1 - produto.desconto / 100))}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        a vista{" "}
                        <span className="font-semibold text-gray-700">
                          {formatarMoeda(produto.valor)}
                        </span>
                      </div>
                    )}
                  </>
                ) : temValorBase ? (
                  precoPromocional ? (
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 line-through">
                        {formatarMoeda(precoPromocional.valorBase)}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatarMoeda(precoPromocional.valorFinal)}
                      </p>
                    </div>
                  ) : produto.desconto !== null ? (
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 line-through">
                        {formatarMoeda(produto.valor)}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatarMoeda(produto.valor * (1 - produto.desconto / 100))}
                      </p>
                    </div>
                  ) : (
                    <p className="text-lg font-bold text-gray-900">
                      {formatarMoeda(produto.valor)}
                    </p>
                  )
                ) : (
                  <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                    Dividimos em ate {produto.maximo_parcelas}x
                  </span>
                )}
              </div>
            ) : null}

            {produto.tamanhos_disponiveis?.trim() ? (
              <div>
                <p className="text-sm text-gray-500">Tamanhos disponiveis:</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {produto.tamanhos_disponiveis.split(",").map((tamanho) => (
                    <span
                      key={tamanho.trim()}
                      className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
                    >
                      {tamanho.trim()}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            <ProdutoAcoes produto={produto} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const ProdutoModal = ({ produto, onClose }) => {
  const loja = useSelector((state) => state.catalogo.loja);
  const fotosOrdenadas = useMemo(
    () => ordenarFotosComCapaPrimeiro(produto?.fotos || []),
    [produto?.fotos]
  );
  const [indiceImagem, setIndiceImagem] = useState(0);
  const [modoVitrine, setModoVitrine] = useState(false);
  const [feedbackModoVitrine, setFeedbackModoVitrine] = useState("");
  const [zoomImagem, setZoomImagem] = useState(1);
  const [panImagemX, setPanImagemX] = useState(0);
  const [pinchAtivo, setPinchAtivo] = useState(false);
  const touchStart = useRef(null);
  const pinchStart = useRef(null);
  const dragStart = useRef(null);
  const zoomImagemAtual = useRef(1);
  const panImagemAtual = useRef(0);
  const ultimoTap = useRef(null);
  const ultimoTogglePorTouch = useRef(0);
  const feedbackTimeout = useRef(null);

  const totalFotos = fotosOrdenadas.length;
  const fotoAtual = fotosOrdenadas[indiceImagem] || null;
  const imagemAtual = fotoAtual?.url
    ? `${process.env.REACT_APP_UPLOADS_URL}${fotoAtual.url}`
    : null;

  const selecionarAnterior = () => {
    if (totalFotos < 2) return;
    setIndiceImagem((indice) => (indice - 1 + totalFotos) % totalFotos);
  };

  const selecionarProxima = () => {
    if (totalFotos < 2) return;
    setIndiceImagem((indice) => (indice + 1) % totalFotos);
  };

  const alternarModoVitrine = () => {
    const proximoEstado = !modoVitrine;
    setModoVitrine(proximoEstado);
    setZoomImagem(1);
    setPanImagemX(0);
    zoomImagemAtual.current = 1;
    panImagemAtual.current = 0;
    setPinchAtivo(false);
    pinchStart.current = null;
    dragStart.current = null;
    setFeedbackModoVitrine(
      proximoEstado
        ? "Modo vitrine ativado · toque duas vezes para voltar"
        : "Controles restaurados"
    );

    window.clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = window.setTimeout(() => {
      setFeedbackModoVitrine("");
    }, 1800);
  };

  useEffect(() => {
    setIndiceImagem(0);
    setModoVitrine(false);
    setZoomImagem(1);
    setPanImagemX(0);
    zoomImagemAtual.current = 1;
    panImagemAtual.current = 0;
    setPinchAtivo(false);
    pinchStart.current = null;
    dragStart.current = null;

    try {
      const dicaJaExibida = window.sessionStorage.getItem(
        "vintree_modo_vitrine_hint"
      );
      if (!dicaJaExibida) {
        setFeedbackModoVitrine(
          "Toque duas vezes na foto para ativar o modo vitrine"
        );
        window.sessionStorage.setItem("vintree_modo_vitrine_hint", "true");
        window.clearTimeout(feedbackTimeout.current);
        feedbackTimeout.current = window.setTimeout(() => {
          setFeedbackModoVitrine("");
        }, 1800);
      }
    } catch {
      // O modo continua funcionando mesmo se o armazenamento estiver indisponivel.
    }
  }, [produto?.id]);

  useEffect(
    () => () => {
      window.clearTimeout(feedbackTimeout.current);
    },
    []
  );

  useEffect(() => {
    setZoomImagem(1);
    setPanImagemX(0);
    zoomImagemAtual.current = 1;
    panImagemAtual.current = 0;
    pinchStart.current = null;
    dragStart.current = null;
    setPinchAtivo(false);
  }, [indiceImagem]);

  useEffect(() => {
    if (!produto) return undefined;

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") {
        setIndiceImagem((indice) =>
          totalFotos > 1 ? (indice - 1 + totalFotos) % totalFotos : indice
        );
      }
      if (event.key === "ArrowRight") {
        setIndiceImagem((indice) =>
          totalFotos > 1 ? (indice + 1) % totalFotos : indice
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, produto, totalFotos]);

  if (!produto) return null;

  if (loja?.layout_card_produto === "classico") {
    return <ProdutoModalClassico produto={produto} onClose={onClose} />;
  }

  const precoPromocional = calcularPrecoPromocional(produto);
  const temValorBase =
    produto.valor !== null &&
    produto.valor !== undefined &&
    produto.valor !== "";
  const temParcelas =
    produto.maximo_parcelas !== null &&
    produto.maximo_parcelas !== undefined &&
    produto.maximo_parcelas !== "";
  const tamanhos = produto.tamanhos_disponiveis
    ? produto.tamanhos_disponiveis
        .split(",")
        .map((tamanho) => tamanho.trim())
        .filter(Boolean)
    : [];
  const limitarPanX = (valor, zoom = zoomImagemAtual.current) => {
    const larguraTela =
      typeof window !== "undefined" ? window.innerWidth : 390;
    const limite = Math.max((zoom - 1) * larguraTela * 0.45, 0);
    return Math.min(Math.max(valor, -limite), limite);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950/55 p-0 backdrop-blur-xl lg:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="produto-modal-titulo"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className={`relative isolate h-[100dvh] w-full select-none overflow-hidden bg-slate-950 shadow-2xl ring-0 lg:h-[calc(100dvh-2rem)] lg:max-w-[1400px] lg:rounded-[2rem] lg:ring-1 lg:ring-white/20 ${
          modoVitrine ? "touch-none" : "touch-manipulation"
        }`}
        onDoubleClick={(event) => {
          if (event.target.closest?.("button, a")) return;
          if (Date.now() - ultimoTogglePorTouch.current < 600) return;
          alternarModoVitrine();
        }}
        onTouchStart={(event) => {
          if (modoVitrine && event.touches.length === 2) {
            const primeiroToque = event.touches[0];
            const segundoToque = event.touches[1];
            pinchStart.current = {
              distancia: Math.hypot(
                segundoToque.clientX - primeiroToque.clientX,
                segundoToque.clientY - primeiroToque.clientY
              ),
              zoomInicial: zoomImagem,
            };
            setPinchAtivo(true);
            touchStart.current = null;
            dragStart.current = null;
            ultimoTap.current = null;
            return;
          }

          const touch = event.touches[0];
          touchStart.current = touch
            ? {
                x: touch.clientX,
                y: touch.clientY,
                iniciadoEm: Date.now(),
              }
            : null;
          dragStart.current =
            modoVitrine && touch && zoomImagemAtual.current > 1
              ? {
                  x: touch.clientX,
                  panInicial: panImagemAtual.current,
                }
              : null;
        }}
        onTouchMove={(event) => {
          if (
            modoVitrine &&
            dragStart.current &&
            event.touches.length === 1
          ) {
            event.preventDefault();
            const touch = event.touches[0];
            const proximoPan = limitarPanX(
              dragStart.current.panInicial + touch.clientX - dragStart.current.x
            );
            panImagemAtual.current = proximoPan;
            setPanImagemX(proximoPan);
            return;
          }

          if (
            !modoVitrine ||
            !pinchStart.current ||
            event.touches.length !== 2
          ) {
            return;
          }

          event.preventDefault();
          const primeiroToque = event.touches[0];
          const segundoToque = event.touches[1];
          const distanciaAtual = Math.hypot(
            segundoToque.clientX - primeiroToque.clientX,
            segundoToque.clientY - primeiroToque.clientY
          );
          const proporcao =
            distanciaAtual / Math.max(pinchStart.current.distancia, 1);
          const novoZoom = Math.min(
            Math.max(pinchStart.current.zoomInicial * proporcao, 1),
            2.6
          );

          zoomImagemAtual.current = novoZoom;
          const panLimitado = limitarPanX(panImagemAtual.current, novoZoom);
          panImagemAtual.current = panLimitado;
          setZoomImagem(novoZoom);
          setPanImagemX(panLimitado);
        }}
        onTouchEnd={(event) => {
          if (pinchStart.current) {
            if (event.touches.length < 2) {
              pinchStart.current = null;
              setPinchAtivo(false);
            }

            touchStart.current = null;
            dragStart.current = null;
            ultimoTap.current = null;
            return;
          }

          if (event.target.closest?.("button, a") || !touchStart.current) {
            touchStart.current = null;
            ultimoTap.current = null;
            return;
          }

          const touch = event.changedTouches[0];
          if (!touch) return;

          const agora = Date.now();
          const deslocamentoX = touch.clientX - touchStart.current.x;
          const deslocamentoY = touch.clientY - touchStart.current.y;
          const duracao = agora - touchStart.current.iniciadoEm;
          touchStart.current = null;

          if (dragStart.current) {
            dragStart.current = null;
            if (Math.abs(deslocamentoX) > 8 || Math.abs(deslocamentoY) > 8) {
              ultimoTap.current = null;
              return;
            }
          }

          if (
            !modoVitrine &&
            totalFotos > 1 &&
            Math.abs(deslocamentoX) > 45 &&
            Math.abs(deslocamentoX) > Math.abs(deslocamentoY)
          ) {
            ultimoTap.current = null;
            if (deslocamentoX > 0) selecionarAnterior();
            else selecionarProxima();
            return;
          }

          if (
            Math.abs(deslocamentoX) > 12 ||
            Math.abs(deslocamentoY) > 12 ||
            duracao > 350
          ) {
            ultimoTap.current = null;
            return;
          }

          const tapAnterior = ultimoTap.current;
          const distanciaEntreTaps = tapAnterior
            ? Math.hypot(
                touch.clientX - tapAnterior.x,
                touch.clientY - tapAnterior.y
              )
            : Infinity;

          if (
            tapAnterior &&
            agora - tapAnterior.ocorridoEm <= 320 &&
            distanciaEntreTaps <= 40
          ) {
            ultimoTap.current = null;
            ultimoTogglePorTouch.current = agora;
            alternarModoVitrine();
            return;
          }

          ultimoTap.current = {
            x: touch.clientX,
            y: touch.clientY,
            ocorridoEm: agora,
          };
        }}
      >
        {imagemAtual ? (
          <>
            <img
              src={imagemAtual}
              alt=""
              aria-hidden="true"
              className="absolute inset-[-5%] h-[110%] w-[110%] scale-110 object-cover opacity-75 blur-2xl brightness-110 saturate-125"
            />
            <div className="absolute inset-0 bg-white/[0.03]" />
            <img
              src={imagemAtual}
              alt={`Foto ${indiceImagem + 1} de ${totalFotos} de ${
                produto.descricao
              }`}
              className={`absolute inset-0 h-full w-full object-contain ease-out ${
                pinchAtivo
                  ? "transition-none"
                  : "transition-transform duration-500"
              }`}
              style={{
                transform: modoVitrine
                  ? `translateX(${panImagemX}px) scale(${zoomImagem})`
                  : "translateX(0px) scale(1)",
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-slate-800 to-slate-950 text-sm uppercase tracking-[0.25em] text-white/50">
            Produto sem foto
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(255,255,255,0.3),transparent_30%),radial-gradient(circle_at_84%_32%,rgba(255,255,255,0.18),transparent_26%)] mix-blend-screen" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[48%] bg-gradient-to-t from-slate-950/80 via-slate-950/30 via-45% to-transparent sm:h-[54%] sm:from-slate-950/85 sm:via-slate-950/40" />

        <div
          aria-hidden={modoVitrine}
          className={`absolute inset-x-0 top-0 z-30 flex items-center justify-between p-4 transition-all duration-300 sm:p-6 ${
            modoVitrine
              ? "pointer-events-none -translate-y-2 opacity-0"
              : "translate-y-0 opacity-100"
          }`}
        >
          <div className="rounded-full border border-white/20 bg-black/15 px-3 py-1.5 text-[11px] font-medium tracking-[0.18em] text-white/90 shadow-lg backdrop-blur-2xl">
            {totalFotos > 0
              ? `${String(indiceImagem + 1).padStart(2, "0")} / ${String(
                  totalFotos
                ).padStart(2, "0")}`
              : "VINTREE"}
          </div>

          <button
            type="button"
            onClick={onClose}
            tabIndex={modoVitrine ? -1 : 0}
            aria-label="Fechar detalhes do produto"
            className="grid h-11 w-11 place-items-center rounded-full border border-white/25 bg-black/15 text-white shadow-lg backdrop-blur-2xl transition hover:scale-105 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
          >
            <HiOutlineX className="h-6 w-6" />
          </button>
        </div>

        {totalFotos > 1 ? (
          <>
            <button
              type="button"
              onClick={selecionarAnterior}
              tabIndex={modoVitrine ? -1 : 0}
              aria-label="Ver foto anterior"
              className={`absolute left-3 top-1/2 z-30 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/15 text-white shadow-xl backdrop-blur-2xl transition-all duration-300 hover:scale-105 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 sm:left-6 sm:h-12 sm:w-12 ${
                modoVitrine
                  ? "pointer-events-none -translate-x-2 opacity-0"
                  : "translate-x-0 opacity-100"
              }`}
            >
              <HiChevronLeft className="h-7 w-7" />
            </button>
            <button
              type="button"
              onClick={selecionarProxima}
              tabIndex={modoVitrine ? -1 : 0}
              aria-label="Ver proxima foto"
              className={`absolute right-3 top-1/2 z-30 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/15 text-white shadow-xl backdrop-blur-2xl transition-all duration-300 hover:scale-105 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 sm:right-6 sm:h-12 sm:w-12 ${
                modoVitrine
                  ? "pointer-events-none translate-x-2 opacity-0"
                  : "translate-x-0 opacity-100"
              }`}
            >
              <HiChevronRight className="h-7 w-7" />
            </button>
          </>
        ) : null}

        <div
          className={`absolute inset-x-0 bottom-0 z-20 overflow-y-auto px-5 pt-16 text-white sm:max-h-[64dvh] sm:px-10 sm:pb-8 sm:pt-28 lg:px-12 lg:pb-9 ${
            modoVitrine
              ? "max-h-[72dvh] pb-[max(7rem,calc(env(safe-area-inset-bottom)+6rem))] sm:pb-[max(2.5rem,env(safe-area-inset-bottom))]"
              : "max-h-[62dvh] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
          }`}
        >
          <div className="mx-auto max-w-7xl">
            {totalFotos > 1 ? (
              <div
                aria-hidden={modoVitrine}
                className={`flex items-center gap-1.5 overflow-hidden transition-all duration-300 ${
                  modoVitrine
                    ? "pointer-events-none mb-0 h-0 translate-y-2 opacity-0"
                    : "mb-3 h-1.5 translate-y-0 opacity-100 sm:mb-5"
                }`}
                aria-label={`Foto ${indiceImagem + 1} de ${totalFotos}`}
              >
                {fotosOrdenadas.map((foto, indice) => (
                  <button
                    key={foto.id || foto.url}
                    type="button"
                    onClick={() => setIndiceImagem(indice)}
                    tabIndex={modoVitrine ? -1 : 0}
                    aria-label={`Ver foto ${indice + 1}`}
                    aria-current={indice === indiceImagem ? "true" : undefined}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      indice === indiceImagem
                        ? "w-8 bg-white"
                        : "w-3 bg-white/35 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            ) : null}

            <div className="grid gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,430px)] lg:items-end lg:gap-8">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-3 sm:gap-2">
                  {produto.marca ? (
                    <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] backdrop-blur-xl sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.14em]">
                      {produto.marca}
                    </span>
                  ) : null}
                  {precoPromocional ? (
                    <span className="rounded-full bg-white px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-black sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.1em]">
                      {precoPromocional.percentual}% off
                    </span>
                  ) : null}
                </div>

                <h2
                  id="produto-modal-titulo"
                  className="max-w-4xl text-base font-medium leading-[1.12] tracking-[-0.01em] text-white drop-shadow-lg sm:text-2xl sm:leading-[1.08] sm:tracking-[-0.015em] lg:text-[clamp(1.65rem,2vw,2rem)]"
                >
                  {produto.descricao}
                </h2>

                {temValorBase || temParcelas ? (
                  <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-0.5 text-white sm:mt-4 sm:gap-x-4 sm:gap-y-1">
                    {temValorBase ? (
                      <div>
                        {precoPromocional ? (
                          <p className="text-[10px] text-white/60 line-through sm:text-sm">
                            {formatarMoeda(precoPromocional.valorBase)}
                          </p>
                        ) : null}
                        <p className="text-base font-semibold sm:text-xl">
                          {formatarMoeda(
                            precoPromocional
                              ? precoPromocional.valorFinal
                              : produto.valor
                          )}
                        </p>
                      </div>
                    ) : null}
                    {temParcelas ? (
                      <p className="pb-0.5 text-xs text-white/70 sm:text-sm">
                        {temValorBase
                          ? `${produto.maximo_parcelas}x de ${formatarMoeda(
                              produto.valor / produto.maximo_parcelas
                            )}`
                          : `Dividimos em ate ${produto.maximo_parcelas}x`}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {tamanhos.length ? (
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5 sm:mt-4 sm:gap-2">
                    <span className="mr-1 text-[9px] font-medium uppercase tracking-[0.12em] text-white/60 sm:text-[10px] sm:tracking-[0.14em]">
                      Tamanhos
                    </span>
                    {tamanhos.map((tamanho) => (
                      <span
                        key={tamanho}
                        className="grid min-h-6 min-w-6 place-items-center rounded-full border border-white/25 bg-white/10 px-1.5 text-[10px] font-medium backdrop-blur-xl sm:min-h-7 sm:min-w-7 sm:px-2 sm:text-[11px]"
                      >
                        {tamanho}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {!modoVitrine ? (
                <ProdutoAcoes produto={produto} variant="editorial" />
              ) : null}
            </div>
          </div>
        </div>

        <div
          aria-live="polite"
          className={`pointer-events-none absolute left-1/2 top-5 z-40 -translate-x-1/2 rounded-full border border-white/20 bg-black/35 px-4 py-2 text-center text-[11px] font-medium tracking-wide text-white shadow-xl backdrop-blur-2xl transition-all duration-300 sm:top-7 ${
            feedbackModoVitrine
              ? "translate-y-0 opacity-100"
              : "-translate-y-2 opacity-0"
          }`}
        >
          {feedbackModoVitrine}
        </div>
      </article>
    </div>
  );
};

export default ProdutoModal;
